import { Component } from '@angular/core';
import { HelpContentComponent, HelpService } from '../../../UI-Lib/Help-Tooltip';
import { TaxableValueInfo } from '../assessment.model';
import { AssessmentService } from '../assessment.service';

export interface AssessmentTaxableValueHelpComponentParams {
    currentTaxableValue: TaxableValueInfo;
    otherTaxableValues: TaxableValueInfo[];
    // Set only in Manual Taxable Value mode: the Tax Authority configuration used to resolve the Tax
    // Authorities mapped to the row.
    manualUsageTaxAuthorities?: Core.TaxAuthorityInfo[];
    // Set while the row is only a preview of the Prior Year Setup: its Tax Authority mappings and tax amount do not
    // exist until the user saves.
    isUnsavedPreview?: boolean;
}

@Component({
    selector: 'assessment-taxable-value-help',
    templateUrl: './assessmentTaxableValueHelp.component.html',
    standalone: false
})
export class AssessmentTaxableValueHelpComponent implements HelpContentComponent<AssessmentTaxableValueHelpComponentParams> {
    constructor(
        private readonly _helpService: HelpService,
        private readonly _assessmentService: AssessmentService) {
    }

    params: AssessmentTaxableValueHelpComponentParams;
    items: string[] = [];
    currentTaxableValue: TaxableValueInfo;
    allTaxableValues: TaxableValueInfo[];
    uniqueTaxableAssessmentNames: string[] = [];
    taxAmount: number;
    adjustmentAssessedValue: number;
    hasConfigurationChanged: boolean;
    isManualUsage: boolean;
    isUnsavedPreview: boolean;

    async helpInit(params: AssessmentTaxableValueHelpComponentParams): Promise<void> {
        this.currentTaxableValue = params.currentTaxableValue;
        this.allTaxableValues = params.otherTaxableValues;
        this.isManualUsage = !!params.manualUsageTaxAuthorities;
        this.isUnsavedPreview = !!params.isUnsavedPreview;

        if (this.isUnsavedPreview) {
            this._helpService.refresh();
            return;
        }

        if (this.isManualUsage) {
            this._populateManualUsage(params.manualUsageTaxAuthorities);
            this._helpService.refresh();
            return;
        }

        this.uniqueTaxableAssessmentNames = !this.currentTaxableValue.hasConfigurationChanged
            ? this._assessmentService.getUniqueValues(this.currentTaxableValue.taxableAssessmentNames ?? [])
            : [];
        this.taxAmount = Math.max(this.currentTaxableValue.taxAmount, 0);
        this.adjustmentAssessedValue = this.currentTaxableValue.adjustmentAssessedValue;
        this.hasConfigurationChanged = this.currentTaxableValue.hasConfigurationChanged;

        this._populateItems();
        this._helpService.refresh();
    }

    private _populateManualUsage(taxAuthorities: Core.TaxAuthorityInfo[]): void {
        const usedTaxAuthorities = new Map<number, Core.TaxAuthorityInfo>();

        taxAuthorities
            .filter((x) => this.currentTaxableValue.taxAuthorityIds?.includes(x.taxAuthorityId))
            .forEach((x) => usedTaxAuthorities.set(x.taxAuthorityId, x));

        const totalRate = [...usedTaxAuthorities.values()].reduce((total, x) => total + x.rate, 0);

        this.items = [...usedTaxAuthorities.values()].map((x) => x.taxAuthorityName).sort((a, b) => a.localeCompare(b));
        this.taxAmount = Math.max((this.currentTaxableValue.assessedValue ?? 0) * totalRate, 0);
    }

    private _populateItems(): void {
        if (this.currentTaxableValue.hasConfigurationChanged) {
            return;
        }

        let categories: string[] = [];
        let taxAuthorityNames: string[] = [];

        for (let i = 0; i < this.currentTaxableValue.taxAuthorityCategories.length; i++) {
            if (this.allTaxableValues.some(x => x.hasConfigurationChanged || x.taxAuthorityCategories.includes(this.currentTaxableValue.taxAuthorityCategories[i]))) {
                taxAuthorityNames.push(this.currentTaxableValue.taxAuthorityNames[i]);
            } else {
                categories.push(this.currentTaxableValue.taxAuthorityCategories[i]);
            }
        }

        categories.sort();
        taxAuthorityNames.sort();

        this.items = this._assessmentService.getUniqueValues([...categories, ...taxAuthorityNames]);
    }
}
